import { Divider, Position } from '@busybusy/webapp-react-ui';
import { useQuery } from '@tanstack/react-query';
import { NotCertifiedIcon } from 'assets/icons';
import classNames from 'classnames';
import IconButton from 'components/foundation/buttons/IconButton/IconButton';
import { IBasicPickerValue } from 'components/foundation/pickers/basic-picker/BasicPicker/BasicPicker';
import DropDownPicker from 'components/foundation/pickers/DropDownPicker/DropDownPicker';
import SimplePickerRow from 'components/foundation/pickers/SimplePickerRow/SimplePickerRow';
import { DEFAULT_PICKER_PAGE_SIZE, findOrFetchPickerItem } from 'components/foundation/pickers/utils';
import { useCertificationChecker } from 'containers/certification-status-report/hooks/useCertificationChecker';
import { useDebounce, useOrganization } from 'hooks';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import { t } from 'i18next';
import { isNil, last } from 'lodash';
import * as React from 'react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { Nullable } from 'types/util/Nullable';
import { useFeatureFlags } from 'utils/features';
import { getEquipmentDisplay } from 'utils/stringUtils';
import { NO_EQUIPMENT_ID } from '../EquipmentClockFormPickerFormField/EquipmentClockFormPickerFormField';
import { useEquipmentPickerData } from './hooks/useEquipmentPickerData';

export interface IEquipmentPickerProps {
  value: string | null;
  onSelect: (equipmentId: string | null, canSubmit?: boolean) => void;
  minWidth?: string;
  placeholder?: string;
  error?: boolean;
  position?: Position;
  dropDownButtonRender?: ReactNode;
  pickerHeaderItems?: IBasicPickerValue[];
  closeButtonRender?: (handleClear: (event: React.KeyboardEvent | React.MouseEvent) => void) => ReactNode;
  className?: ClassName;
  filteredIds?: string[];
  filterAddOns?: boolean;
  memberIds?: string[];
  showMissingCertWarning?: boolean;
  onClear?: (canSubmit: boolean) => void;
}

export interface ICertificationRecordComparisonObject {
  memberId: string;
  certificationIds: string[];
}

const EquipmentPicker = ({
  value: valueProp,
  onSelect,
  minWidth = '350px',
  error,
  placeholder,
  position,
  closeButtonRender,
  dropDownButtonRender,
  className,
  pickerHeaderItems,
  filteredIds,
  filterAddOns = true,
  memberIds,
  showMissingCertWarning = false,
  onClear,
}: IEquipmentPickerProps) => {
  const organization = useOrganization();
  const isCertificationStatusEnabled = useFeatureFlags('CERTIFICATIONS');
  const { getMissingEquipmentCertifications } = useCertificationChecker();
  const [missingCertification, setMissingCertification] = useState<boolean>(false);
  const baseQueryKey = useReactQueryBaseKey();

  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchDebounce = useDebounce(searchValue, 250);
  const scroller = useRef<HTMLElement>();

  const { fetchPage, fetchSelected } = useEquipmentPickerData({ filteredIds, filterAddOns });

  function onClose() {
    setIsOpen(false);
    setSearchValue('');
  }

  const {
    data: queriedData,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller.current,
    [...baseQueryKey, 'equipment-picker', searchDebounce, filteredIds, filterAddOns],
    (after, first) => fetchPage(after, first, searchDebounce),
    DEFAULT_PICKER_PAGE_SIZE,
    { enabled: isOpen }
  );

  const checkCertifications = async (equipmentId: string | null): Promise<boolean> => {
    if (!isNil(equipmentId) && isCertificationStatusEnabled && memberIds?.length) {
      const results = await getMissingEquipmentCertifications(memberIds, [equipmentId]);
      const hasMissingCerts = results.some((member) => member.missingCerts.length > 0);
      setMissingCertification(hasMissingCerts);
      return !hasMissingCerts;
    }
    setMissingCertification(false);
    return true;
  };

  const checkCanSubmit = async (value: string | null) => {
    if (memberIds && value) {
      const canSubmit = await checkCertifications(value);
      onSelect(value, canSubmit);
    } else {
      onSelect(value, true);
    }
  };

  async function handleSelect(value: Nullable<IBasicPickerValue<string>>) {
    checkCanSubmit(value?.id ?? null);
  }

  useEffect(() => {
    checkCanSubmit(valueProp);
  }, [memberIds]);

  const { data: selectedEquipment } = useQuery({
    queryKey: [...baseQueryKey, 'equipment-picker-by-id', valueProp],
    queryFn: () => findOrFetchPickerItem(valueProp!, queriedData, fetchSelected),
    initialData: null,
    enabled: !!valueProp && valueProp !== NO_EQUIPMENT_ID,
  });

  const data = React.useMemo((): Array<IBasicPickerValue<string>> => {
    const items: Array<IBasicPickerValue<string>> = pickerHeaderItems ? [...pickerHeaderItems] : [];
    if (queriedData) {
      items.push(...queriedData.map((e) => ({ id: e.id, name: getEquipmentDisplay(e), cursor: e.cursor })));
    }
    return items;
  }, [pickerHeaderItems, queriedData]);

  function renderValueTemplate() {
    if (valueProp === NO_EQUIPMENT_ID) {
      return <div className="ellipsis">{t('No Equipment')}</div>;
    }

    if (selectedEquipment) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div className="ellipsis">{getEquipmentDisplay(selectedEquipment)}</div>
          {(showMissingCertWarning || missingCertification) && (
            <IconButton
              svg={NotCertifiedIcon}
              tooltipLabel={t(
                'The required certification to operate this equipment is either missing or expired for one or more selected employees.'
              )}
            />
          )}
        </div>
      );
    }
    return <></>;
  }

  function renderRow(item: IBasicPickerValue<string>) {
    const isLastHeaderItem = item.id === last(pickerHeaderItems)?.id;
    if (isLastHeaderItem) {
      return (
        <>
          <SimplePickerRow value={item.name} searchValue={searchValue} />
          <Divider className="my-2" />
        </>
      );
    }
    return <SimplePickerRow value={item.name} searchValue={searchValue} />;
  }

  if (!organization.trackEquipment) {
    return null;
  }

  const classes = classNames('equipment-picker', className);

  return (
    <DropDownPicker
      value={valueProp}
      dataError={isDataError}
      error={error}
      data={data}
      loadedAll={loadedAll}
      onSelect={handleSelect}
      onClose={onClose}
      onOpen={() => setIsOpen(true)}
      onSearch={setSearchValue}
      searchValue={searchValue}
      valueTemplate={renderValueTemplate()}
      renderRow={renderRow}
      minWidth={minWidth}
      position={position}
      placeholder={placeholder}
      className={classes}
      closeButtonRender={closeButtonRender}
      dropDownButtonRender={dropDownButtonRender}
      defaultFocusIndex={pickerHeaderItems?.length ?? 0}
      setScrollerRef={(scrollerRef) => (scroller.current = scrollerRef)}
      isLoading={isLoading}
      onClear={() => onClear?.(true)}
    />
  );
};

export default EquipmentPicker;
